// The API owns the 30-day warning. This module only decides what the shell shows.

export const MODULE_TOOLS = {
  'msel-doctor': 'msel_doctor',
  'metl-smith': 'metl_smith',
  baron: 'baron',
  scribe: 'scribe',
} as const

export type ModuleName = keyof typeof MODULE_TOOLS

export const KNOWN_TOOLS = ['msel_doctor', 'metl_smith', 'baron', 'scribe', 'system_data'] as const

export type ToolId = (typeof KNOWN_TOOLS)[number]

const DEFAULT_OFF_TOOLS = new Set<ToolId>(['system_data'])

export const TOOL_LABELS: Record<ToolId, string> = {
  msel_doctor: 'MSEL Doctor',
  metl_smith: 'METL Smith',
  baron: 'Baron',
  scribe: 'Scribe',
  system_data: 'System Data',
}

export interface ToolComponentState {
  licensed: boolean
  active: boolean
  overridden: boolean
}

export interface LicenseState {
  configured: boolean
  type: string | null
  starts_on: string | null
  expires_on: string | null
  exercise_id: string | null
  tools: string[]
  licensed_tools?: string[]
  active_tools?: string[]
  components?: Partial<Record<ToolId, ToolComponentState>>
  extension_on: string | null
  effective_expires_on: string | null
  days_remaining: number | null
  expired: boolean
  warning: boolean
  viewer_is_erudio_admin: boolean
  viewer_is_client_admin: boolean
}

export interface ComponentRow extends ToolComponentState {
  id: ToolId
  label: string
}

export function showExpiryLockout(state: LicenseState | null | undefined): boolean {
  return Boolean(state?.configured && state.expired && !state.viewer_is_erudio_admin)
}

export function showClientAdminWarning(state: LicenseState | null | undefined): boolean {
  return Boolean(
    state?.configured && state.warning && state.viewer_is_client_admin && !state.expired,
  )
}

function toolId(moduleName: string): ToolId | undefined {
  return MODULE_TOOLS[moduleName as ModuleName]
}

export function toolAllowed(state: LicenseState | null | undefined, moduleName: string): boolean {
  if (!state || state.viewer_is_erudio_admin) return true
  const tool = toolId(moduleName)
  if (!tool) return true
  if (Array.isArray(state.active_tools)) return state.active_tools.includes(tool)
  if (!state.configured) return true
  return state.tools.includes(tool)
}

/** Erudio admins can open an inactive component. The shell marks it Off. */
export function toolMarkedOff(state: LicenseState | null | undefined, moduleName: string): boolean {
  if (!state?.viewer_is_erudio_admin) return false
  const tool = toolId(moduleName)
  if (!tool) return false
  if (Array.isArray(state.active_tools)) return !state.active_tools.includes(tool)
  if (!state.configured) return false
  return !state.tools.includes(tool)
}

export function systemDataActive(state: LicenseState | null | undefined): boolean {
  if (Array.isArray(state?.active_tools)) return state.active_tools.includes('system_data')
  return Boolean(state?.components?.system_data?.active)
}

/** Erudio admins always see System Data. Other admins see it only while it is on. */
export function showSystemDataNav(
  state: LicenseState | null | undefined,
  roleAdmin: boolean,
): boolean {
  if (state?.viewer_is_erudio_admin) return true
  return roleAdmin && systemDataActive(state)
}

export function systemDataMarkedOff(state: LicenseState | null | undefined): boolean {
  return Boolean(state?.viewer_is_erudio_admin && !systemDataActive(state))
}

export function componentRows(state: LicenseState | null | undefined): ComponentRow[] {
  return KNOWN_TOOLS.map((id) => {
    const fromApi = state?.components?.[id]
    if (fromApi) {
      return { id, label: TOOL_LABELS[id], ...fromApi }
    }
    const licensed = Boolean(state?.licensed_tools?.includes(id) || state?.tools.includes(id))
    const active = Array.isArray(state?.active_tools)
      ? state.active_tools.includes(id)
      : DEFAULT_OFF_TOOLS.has(id)
        ? licensed
        : !state?.configured || licensed
    return { id, label: TOOL_LABELS[id], licensed, active, overridden: false }
  })
}
